import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Terminal, Crown, Check, ArrowRight, Lock, Sparkles, ChevronLeft,
  Loader2, ShieldCheck, Mail, Zap,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.13e — Founding Customer programme (anchored to new $1,499 Scale tier).
// Public invite-anchored landing page: $1,049/mo lifetime-locked (30% off Scale) for first 25.
// Phase 25.13g — Vertical-keyed onboarding via `?vertical=<slug>` URL param.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

const INDUSTRIES = [
  "Retail", "Hospitality", "Food & Beverage", "Consumer Goods",
  "Technology", "Finance", "Logistics", "Manufacturing",
  "Training & Education", "Other",
];

// Vertical-keyed copy bundle. Slug must match backend INDUSTRY_PACKS keys.
const VERTICALS = {
  "rope-access": {
    eyebrow: "// FOUNDING CUSTOMER · ROPE-ACCESS TRAINING EDITION",
    headlineTop: "Built for IRATA training providers.",
    headlineBottom: "$1,049/mo, locked for life.",
    lede: (
      <>
        The public Scale tier is <strong className="text-white">$1,499/month</strong>. The
        first 25 rope-access training providers who commit now get <strong className="text-white">
        30% off — locked permanently</strong> at $1,049/month. Your workspace will be pre-loaded
        with rope-access training templates: re-certification revenue cards, bay-utilisation
        heatmap placeholders, corporate-pipeline scoring, and wind-farm project intelligence —
        all activating the moment you upload your trainee CSV and connect Stripe + Xero.
      </>
    ),
    industry: "Training & Education",
    perksAdditional: [
      { icon: Crown, headline: "Rope-access industry templates pre-loaded", sub: "Re-cert revenue card · bay heatmap placeholders · corporate pipeline scoring · wind-farm intel." },
    ],
  },
};

const BASE_PERKS = [
  { icon: Lock, headline: "$1,049/month, locked for life", sub: "30% off the public Scale price ($1,499/mo) — locked even as our pricing climbs." },
  { icon: Crown, headline: "Founding Member badge", sub: "Visible in your workspace + your name on the public Wall of Fame at /founding." },
  { icon: Sparkles, headline: "Direct line to the founder", sub: "Slack / email channel for product feedback. Your priorities get built." },
  { icon: Zap, headline: "Early access to every module", sub: "30 days before public launch. Mining, Hospitality, Hedge Ledger v2 — you see it first." },
  { icon: ShieldCheck, headline: "14-day free trial", sub: "No card required to start. Cancel any time during trial, no charge." },
  { icon: Mail, headline: "First case study spot", sub: "Co-authored case study on daraos.ai. You get marketing exposure. We get social proof." },
];

export default function Founding() {
  usePageview("/founding");
  const [searchParams] = useSearchParams();
  const verticalSlug = (searchParams.get("vertical") || "").trim().toLowerCase() || null;
  const vertical = verticalSlug ? VERTICALS[verticalSlug] : null;
  const PERKS = useMemo(
    () => (vertical?.perksAdditional ? [...vertical.perksAdditional, ...BASE_PERKS] : BASE_PERKS),
    [vertical]
  );

  const [avail, setAvail] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", password: "", company: "",
    industry: vertical?.industry || "Retail",
    email_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const loadAvailability = () => {
    fetch(`${BACKEND}/api/public/founding/availability`)
      .then((r) => r.json())
      .then(setAvail)
      .catch(() => setAvail({ taken: 0, total: 25, remaining: 25, full: false }));
  };
  const loadMembers = () => {
    fetch(`${BACKEND}/api/public/founding/members`)
      .then((r) => r.json())
      .then((d) => setMembers(d.members || []))
      .catch(() => {});
  };
  useEffect(() => {
    loadAvailability();
    loadMembers();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email_consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/founding/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, vertical: verticalSlug }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Signup failed");
      localStorage.setItem("dara_token", data.token);
      toast.success(
        `You're founding member #${data.founding_member_number}. Welcome to DARA.`,
        { duration: 7000 }
      );
      setTimeout(() => { window.location.href = "/app"; }, 1500);
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = avail?.remaining ?? null;
  const taken = avail?.taken ?? null;
  const total = avail?.total ?? 25;
  const full = avail?.full ?? false;
  const pctFull = total > 0 ? Math.round(((taken ?? 0) / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="founding-page">
      {/* Top nav */}
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link
            to="/pricing"
            className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2"
          >
            ALL PLANS →
          </Link>
        </div>
      </header>

      {/* Hero — counter front and center */}
      <section className="border-b border-zinc-900 py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-4">
            <Crown className="w-3 h-3" />
            {vertical ? vertical.eyebrow : "// FOUNDING CUSTOMER PROGRAMME · LIMITED TO 25"}
          </div>

          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {vertical ? (
              <>
                {vertical.headlineTop}<br />
                <span className="text-[#FFB000]">{vertical.headlineBottom}</span>
              </>
            ) : (
              <>
                Lock in $1,049/month<br />
                <span className="text-[#FFB000]">for life.</span>
              </>
            )}
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-6 leading-relaxed">
            {vertical ? vertical.lede : (
              <>
                The public Scale tier is <strong className="text-white">$1,499/month</strong>. The first 25
                customers who commit now get <strong className="text-white">30% off — locked permanently</strong>
                {" "}at $1,049/month, even as our public pricing climbs. You'll also get a founder badge,
                early access to every new module, and a direct line to me for product feedback.
              </>
            )}
          </p>

          {/* Live counter */}
          <div
            className="mt-8 border border-[#FFB000]/40 bg-[#FFB000]/[0.04] p-5 sm:p-6"
            data-testid="founding-counter"
          >
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-1">
                  // SPOTS REMAINING
                </div>
                {avail === null ? (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-mono text-xs">Loading…</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading font-black text-5xl sm:text-6xl tracking-tight text-white">
                      {remaining}
                    </span>
                    <span className="font-mono text-sm text-zinc-500">of {total}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                  // LOCKED PRICE
                </div>
                <div className="font-heading font-black text-3xl tracking-tight">$1,049<span className="text-base font-mono text-zinc-500">/mo</span></div>
              </div>
            </div>
            {/* Progress bar */}
            {avail && (
              <div className="mt-5">
                <div className="h-1.5 bg-zinc-900 overflow-hidden">
                  <div
                    className="h-full bg-[#FFB000] transition-all duration-700"
                    style={{ width: `${pctFull}%` }}
                  />
                </div>
                <div className="font-mono text-[10px] text-zinc-600 mt-2 flex justify-between">
                  <span>{taken} CLAIMED</span>
                  <span>{pctFull}% FULL</span>
                </div>
              </div>
            )}
          </div>

          {/* Sold-out fallback */}
          {full && (
            <div
              className="mt-6 border border-[#FF6B47]/40 bg-[#FF6B47]/[0.05] p-5"
              data-testid="founding-sold-out"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF6B47] mb-2">
                // SOLD OUT
              </div>
              <div className="font-heading font-bold text-lg">
                All 25 founding spots have been claimed.
              </div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                You can still get the full DARA experience on the public Scale plan at $1,499/month —
                same product, same DARA-grounded briefings, no lifetime lock.
              </p>
              <Link
                to="/pricing"
                data-testid="founding-soldout-pricing"
                className="mt-4 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:opacity-90"
              >
                SEE SCALE — $1,499/MO
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Perks grid */}
      <section className="py-14 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // WHAT YOU GET
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-10">
            Six things a regular customer never gets.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PERKS.map((p, i) => (
              <div
                key={i}
                data-testid={`founding-perk-${i}`}
                className="border border-zinc-900 bg-zinc-950/40 p-5 flex items-start gap-3"
              >
                <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
                  <p.icon className="w-4 h-4 text-[#FFB000]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base">{p.headline}</div>
                  <div className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup form */}
      {!full && (
        <section className="py-14 border-b border-zinc-900 bg-zinc-950/30">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
              // CLAIM YOUR SPOT
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-6">
              30 seconds. No card needed.
            </h2>

            <form
              onSubmit={submit}
              data-testid="founding-form"
              className="border border-zinc-800 bg-black p-6 space-y-4"
            >
              <Field label="FULL NAME *">
                <input
                  required value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="founding-name"
                  placeholder="Aisha Patel"
                  className={inputCls}
                />
              </Field>
              <Field label="WORK EMAIL *">
                <input
                  type="email" required value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="founding-email"
                  placeholder="aisha@yourcompany.com"
                  className={inputCls}
                />
              </Field>
              <Field label="PASSWORD * (MIN 6)">
                <input
                  type="password" required minLength={6} value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="founding-password"
                  placeholder="••••••"
                  className={inputCls}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="COMPANY *">
                  <input
                    required value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                    data-testid="founding-company"
                    placeholder="Tata Steel"
                    className={inputCls}
                  />
                </Field>
                <Field label="INDUSTRY">
                  <select
                    value={form.industry}
                    onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                    data-testid="founding-industry"
                    className={inputCls}
                  >
                    {INDUSTRIES.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Consent (Phase 25.8) */}
              <label
                className="flex items-start gap-3 cursor-pointer border border-zinc-800 bg-zinc-950/40 p-4 hover:border-zinc-700 transition-colors"
                data-testid="founding-consent-row"
              >
                <input
                  type="checkbox"
                  checked={form.email_consent}
                  onChange={(e) => setForm({ ...form, email_consent: e.target.checked })}
                  data-testid="founding-consent-checkbox"
                  required
                  className="mt-1 accent-[#FFB000] w-4 h-4 flex-shrink-0 cursor-pointer"
                />
                <div className="text-xs text-zinc-300 leading-relaxed">
                  <strong className="text-white">
                    I agree to receive DARA briefings, product emails, and my founding-member welcome.
                  </strong>{" "}
                  <span className="text-zinc-500">
                    One-click unsubscribe in every email.{" "}
                    <Link to="/consent" target="_blank" className="text-[#FFB000] hover:underline">
                      Email policy
                    </Link>.
                  </span>
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting || !form.email_consent}
                data-testid="founding-submit"
                className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    PROVISIONING…
                  </>
                ) : (
                  <>
                    <Crown className="w-3.5 h-3.5" />
                    CLAIM FOUNDING SPOT · LOCK $1,049/MO FOR LIFE
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>

              <div className="font-mono text-[10px] text-zinc-500 text-center pt-2">
                14-day free trial · no card required to start · consent timestamp logged
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Wall of Fame */}
      <section className="py-14 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // WALL OF FAME · FOUNDING MEMBERS
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-8">
            {members.length === 0 ? "Be the first one." : `${members.length} pioneer${members.length === 1 ? "" : "s"} in.`}
          </h2>

          {members.length === 0 && (
            <p className="text-sm text-zinc-500 italic">
              No founding members yet. Claim spot #1 above — your name goes here, permanently.
            </p>
          )}

          {members.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="founding-wall">
              {members.map((m) => (
                <div
                  key={m.number}
                  className="border border-zinc-900 bg-zinc-950/40 p-3"
                  data-testid={`founding-wall-${m.number}`}
                >
                  <div className="font-mono text-[9px] text-[#FFB000] tracking-widest">
                    #{String(m.number).padStart(2, "0")}
                  </div>
                  <div className="font-heading font-bold text-sm mt-1 truncate">{m.first_name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5 truncate">{m.company}</div>
                </div>
              ))}
              {/* Empty slot placeholders */}
              {Array.from({ length: Math.max(0, total - members.length) }).slice(0, 10).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="border border-dashed border-zinc-900 bg-black p-3 opacity-50"
                >
                  <div className="font-mono text-[9px] text-zinc-700 tracking-widest">
                    #{String(members.length + i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-700 mt-1">OPEN</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Link
            to="/pricing"
            data-testid="founding-back-pricing"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> SEE ALL PRICING TIERS
          </Link>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "mt-1.5 w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
